/**
 * Unified Login helper functions
 * Contains logic for initializing and managing unified login flows
 */
import fetch from 'globals/wa-fetch';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import { setUser } from 'stores/user-store';
import { fetchCompany, setCompany } from 'stores/company-store';
import { updateFeaturesList } from 'state/features-list/actions';
import { getStore } from 'hermes-redux';
import * as Storage from 'globals/storage';
import {
  logout,
  setSessionLoadingCompleted,
  updateUser,
} from 'state/session/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import { showToast } from 'toaster-comp/state/actions';
import {
  goTo,
  getUrlParameter,
  getSubdomainFromHost,
  getLandingPageUrl,
} from 'router/navigation';
import { runOnWebWorker } from 'app/run-on-web-worker';
import { startPostLoginFlow } from 'state/post-login/actions';
import { SIGNUP_CODES } from 'pages/signup/constants';
import { updateData } from 'pages/signup/main/state/actions';
import { AppLogger, LogTypes } from '@workivate/tho-web-shared';
import queryString from 'query-string';
import {
  setRedirectionModel,
  shouldResumePostLoginFlow,
} from 'app/state/consent/actions';
import Language from 'language';
import {
  EXCLUDE_FROM_ORIGINAL_PATH,
  PRESERVE_ON_ROOT_PATH,
} from 'app/run-with-usl.constants';
import { isPublicRoute } from 'router/helpers';
import { SIGN_UP_CONSENT_MODULE_NAME } from 'pages/consent/constants';

type RegionEnabledFeatures = {
  is_unified_login_enabled: boolean;
  is_unified_sso_sign_up_enabled: boolean;
};

type RegionData = { region: string; enabled_features: RegionEnabledFeatures };

export const handleLangUpdateDuringLogin = async user => {
  const langQueryString = getUrlParameter('lang');
  const { dispatch } = getStore();

  // By default setLoginLanguagePersistence is set to false unless the user changes language
  // If the 'shouldPersistLoginPageLanguage' storage value is true we will need to make an API call to permanently set the user locale
  // And reload translations
  if (Language.getLoginLanguagePersistence()) {
    // If the stored local is not the same as the user locale, we need to update it (unless it's a shared account)
    if (Language.getLocale() !== user.locale) {
      // For all user types except for shared users we should update the user locale - See UM-1695 for the original issue
      if (!user.is_shared_account) {
        await dispatch(
          updateUser({ locale: Language.getLocale() }, false, false),
        );
      }

      Language.loadTranslations(Language.getLocale());
    }
  } else {
    // If the locale should not be persisted, we will perform a one-time update
    await Language.set({
      locale: langQueryString || user.locale,
      isSharedAccount: user.is_shared_account,
      shouldLoadAllTranslations: true,
    });
  }
};

/**
 * Sets the current path (pathname + filtered search params + hash) for redirect after login or signup
 * Uses browser URL utilities for robust URL handling and filtering
 * Filters out authentication-related parameters using EXCLUDED_REDIRECT_QUERY_PARAMS
 */
export const setOriginalPath = () => {
  try {
    const urlParams = queryString.parse(location.search);

    // Check if originalPath was passed as a query parameter (from subdomain redirect)
    // after having been stored on a generic subdomain by this same function
    // The originalPath will have been sanitized before being stored (below)
    // so we trust the parameter value will not be root or a public path and do not revalidate here
    if (urlParams.originalPath) {
      Storage.set(
        Storage.storageKeys.originalPath,
        urlParams.originalPath,
        false,
      );
      return; // We don't need to remove originalPath from the URL. goTo() will handle it when navigating
    }

    const url = new URL(window.location.href);

    // Remove excluded authentication parameters
    EXCLUDE_FROM_ORIGINAL_PATH.forEach(param => {
      url.searchParams.delete(param);
    });

    const pathname = url.pathname;

    // Special handling for root path: preserve specific query params
    // Storing a root path may cause issues
    // but we need to preserve certain params for external authentication flows (e.g., Zendesk)
    if (pathname === '/') {
      const paramsToPreserve: Record<string, string> = {};

      PRESERVE_ON_ROOT_PATH.forEach(param => {
        const value = url.searchParams.get(param);
        if (value) {
          paramsToPreserve[param] = value;
        }
      });

      // Store preserved params if any exist
      if (Object.keys(paramsToPreserve).length > 0) {
        Storage.set(
          Storage.storageKeys.preservedRootParams,
          JSON.stringify(paramsToPreserve),
          false,
        );
      }

      return; // Still skip storing root paths
    }

    // Don't save public routes (auth/signup flows, error pages, etc.)
    // we do not want to redirect back to /signup if the user followed a route to /signup
    if (isPublicRoute(pathname)) {
      return;
    }

    // Get the relative path (pathname + filtered search params + hash)
    const relativePath = url.href.substring(url.origin.length);

    // Store the relative path for post-login redirect
    Storage.set(Storage.storageKeys.originalPath, relativePath, false);
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Failed to parse current URL for path storage',
      level: LogTypes.Error,
      error: error,
      context: { url: window.location.href },
      shouldLogToDatadog: true,
    });
  }
};

/**
 * Gets and clears the stored original path
 * Returns the stored path and removes it from storage (one-time use)
 * Also checks for preserved root params (e.g., return_to for Zendesk) and reconstructs the URL
 */
export const getAndClearOriginalPath = () => {
  const originalPath: string =
    Storage.get(Storage.storageKeys.originalPath, false) || '';
  const preservedParamsJson: string =
    Storage.get(Storage.storageKeys.preservedRootParams, false) || '';

  // Clear both storage items after reading (one-time use)
  Storage.remove(Storage.storageKeys.originalPath);
  Storage.remove(Storage.storageKeys.preservedRootParams);

  // Prioritize originalPath (which already includes any query params from that page)
  if (originalPath) {
    return originalPath;
  }

  // Only use preserved root params if no originalPath exists
  if (preservedParamsJson) {
    try {
      const preservedParams = JSON.parse(preservedParamsJson);
      const queryParams = new URLSearchParams(preservedParams);
      return `/?${queryParams.toString()}`;
    } catch (error) {
      AppLogger.logWithOptions({
        message: 'Failed to parse preserved root params',
        level: LogTypes.Error,
        error: error,
        context: { preservedParamsJson },
        shouldLogToDatadog: true,
      });
    }
  }

  return '';
};

/**
 * Gets the redirect path after authentication (login or signup) completion
 * Checks for a saved originalPath first, then falls back to feature-based landing page
 * Note the landing page will be a route name and must be used with goTo()
 * (goTo() accepts both pathnames and route names)
 *
 * Used in:
 * - Login flows (after successful authentication)
 * - Signup flows (after account creation and consent acceptance)
 * - Consent completion (both signup and JIT consents)
 *
 * @returns The path to redirect to (either saved originalPath or feature-based landing)
 */
export const getPostAuthRedirectPath = (): string => {
  const originalPath = getAndClearOriginalPath();

  return originalPath || getLandingPageUrl() || '';
};

/**
 * Initialises the login process from an app perspective
 * Sets user and company into the redux store
 */
export const initUnifiedLogin = async () => {
  const { dispatch } = getStore();
  // Get user info, set the user in the redux store and fetch company info
  try {
    const { body: user } = await fetch('/user/me?exclude_field=badges', {
      body: undefined,
      method: 'get',
      oauth2token: true,
    });

    const company = await fetchCompany(user.company_id);
    setCompany(company.body, true);

    await handleLangUpdateDuringLogin(user);

    AppLogger.logWithOptions({
      message: `User signup_state during initUnifiedLogin: ${user['signup_state']}`,
      context: { signup_state: user['signup_state'] },
      shouldLogToDatadog: true,
    });

    if (user['signup_state'] === SIGNUP_CODES.SSO_SIGNUP_PROVISIONED) {
      // An SSO user who has been auto-provisioned and needs to complete the signup flow
      // strictly they only need to accept consents but we only show consents after they signup form has been completed
      dispatch(
        updateData('initData', {
          user,
          signup_state: user['signup_state'],
        }),
      );

      // Defer post-login flow (including consents) until after the user completes the signup form
      // This prevents the consent modal or walkthrough from appearing over the signup form during IDP-initiated SSO
      await dispatch(shouldResumePostLoginFlow(true));
      goTo('signup');
    } else if (user['signup_state'] === SIGNUP_CODES.PENDING_CONSENT) {
      // A user who has completed Sign Up but needs to accept consents
      await setUser(user);
      await dispatch(shouldResumePostLoginFlow(true));

      // Set redirection model for consent container
      dispatch(
        setRedirectionModel({
          type: 'POP_UP',
          onCompleteGoTo: {
            name: getPostAuthRedirectPath(),
            params: {},
          },
        }),
      );

      // Redirect to sign-up consents page
      goTo(SIGN_UP_CONSENT_MODULE_NAME);
    } else {
      // Note that we only update features / set user if the user is NOT in the SSO signup provisioned state because that state should not see the navigation menu
      // Although the features list we pass here is used to build the menu
      // the permissions for the menu are checked against the user object from the Redux store
      // This could perhaps be improved when we come to remove the legacy login
      // as it is not obvious that we need to both set the user and THEN pass the same features list to rebuild the menu
      await setUser(user);
      if (user.features_list.length !== 0) {
        await dispatch(updateFeaturesList(user.features_list));
      }

      // redirect to a saved pathname if it exists
      const originalPath = getAndClearOriginalPath();

      if (originalPath) {
        goTo(originalPath);
      }

      runOnWebWorker(startPostLoginFlow);
    }
  } catch (error) {
    // show an error toast but don't log the user out.
    // Lack of user or company info is a serious problem but they may be able to continue using the app
    // in particular if this is a shared account where company info may not be so important
    dispatch(
      showToast({
        type: ToastType.ERROR,
        message: polyglot.t('global.init_user_error'),
      }),
    );
    AppLogger.logWithOptions({
      message: 'Error during initUnifiedLogin',
      level: LogTypes.Error,
      error: error,
      shouldLogToDatadog: true,
    });
  }

  await dispatch(setSessionLoadingCompleted());
};

/**
 * Sets the value to localStorage
 * @param isUnifiedLoginEnabled
 */
export const setUnifiedLoginEnabled = (isUnifiedLoginEnabled: boolean) => {
  localStorage.setItem(
    Storage.storageKeys.isUnifiedLoginEnabled,
    JSON.stringify(isUnifiedLoginEnabled),
  );
};

/**
 * If we are in a production environment, we will assume that USL is enabled
 * If we are in a non-production environment, we will check if the URL parameter 'isUnifiedLoginEnabled' exists
 * If there is no URL parameter, we will use a LocalStorage value
 */
export const getIsUnifiedLoginEnabled = (): boolean => {
  const urlParams = queryString.parse(location.search, { parseBooleans: true });
  const isUnifiedLoginEnabledParam = urlParams['isUnifiedLoginEnabled'];

  let effectiveIsUnifiedLoginEnabled = true;
  try {
    const isEnabledInProduction = true;
    if (IS_PRODUCTION) {
      return isEnabledInProduction;
    }
    // Check if we have a URL parameter that overrides the default value
    if (isUnifiedLoginEnabledParam !== undefined) {
      const paramValue =
        isUnifiedLoginEnabledParam === 'true' ||
        isUnifiedLoginEnabledParam === '"true"' ||
        isUnifiedLoginEnabledParam === "'true'";

      return paramValue;
    }
    // If no URL parameter, check localStorage
    const localStorageUslEnabled = localStorage.getItem(
      Storage.storageKeys.isUnifiedLoginEnabled,
    );

    if (localStorageUslEnabled !== null) {
      effectiveIsUnifiedLoginEnabled =
        localStorageUslEnabled === 'true' ||
        localStorageUslEnabled === '"true"' ||
        localStorageUslEnabled === "'true'";
    }
    return effectiveIsUnifiedLoginEnabled;
  } catch (e) {
    // If anything fails in this function, assume USL is enabled
    AppLogger.logWithOptions({
      message: 'Error in getIsUnifiedLoginEnabled, defaulting to USL enabled',
      level: LogTypes.Error,
      error: e,
      shouldLogToDatadog: true,
    });
    return true;
  }
};

/**
 * Performs the tenancy lookup based on the provided key and value
 * The response is used to set the correct regional API endpionts for further requests
 * This is why we want to ensure that the tenancy lookup is performed before any other API calls
 * If we have an existing tenancyHash in localStorage, we will use that to perform the lookup
 * Otherwise, we will use the subdomain from the host
 * If the tenancy lookup fails, we will log the user out
 */
export const callTenancyLookup = async () => {
  const { dispatch } = getStore();

  try {
    const tenancyHash = localStorage.getItem(lookupKeys.tenancyHash);
    let lookUpResponse;

    // Make a call to the tenancy lookup API to set the correct endpoints
    if (tenancyHash) {
      lookUpResponse = await performTenancyLookup(
        lookupKeys.tenancyHash,
        tenancyHash,
      );
    } else {
      // If tenancyHash is not set, we will use the domain to perform the lookup
      lookUpResponse = await performTenancyLookup(
        lookupKeys.domain,
        getSubdomainFromHost(),
      );
    }
    if (!lookUpResponse) {
      AppLogger.logWithOptions({
        message: 'Tenancy lookup failed, no response received',
        level: LogTypes.Error,
        shouldLogToDatadog: true,
      });
      await dispatch(logout({ isHard: false }));
    }
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Error in performTenancyLookup',
      level: LogTypes.Error,
      error: error,
      shouldLogToDatadog: true,
    });
    await dispatch(logout({ isHard: false }));
  }
};
